import { useState, useMemo } from "react";
import { Download, Printer, Receipt } from "lucide-react";
import { PageHead, Panel, Kpi, Tag, EmptyState, Button, DataTable, Td, MiniBars, Drawer, downloadFile, toCsv, money } from "@/webapp/fleet-owners/ui";

const billable = (r) => r.status !== "cancelled";
const payState = (r) => (Number(r.paid) ? ["paid", "ok"] : r.status === "completed" ? ["awaiting", "warn"] : ["open", "info"]);
const COLS = [["ref", (r) => r.ref], ["operator", (r) => r.operator], ["site", (r) => r.site_name], ["stall", (r) => r.stall], ["vehicle", (r) => r.vehicle], ["type", (r) => (r.channel === "B2B" ? "monthly" : "overnight")], ["start", (r) => r.start_date], ["end", (r) => r.end_date], ["amount", (r) => r.amount], ["status", (r) => r.status], ["payment", (r) => payState(r)[0]], ["booked_at", (r) => r.created_at]];

function group(rows, key, fmt) {
  const m = new Map();
  for (const r of rows) m.set(key(r), (m.get(key(r)) || 0) + Number(r.amount || 0));
  return [...m.entries()].map(([label, value]) => ({ label: fmt ? fmt(label) : label, value, display: money(value) }));
}

function ReceiptView({ r, company, onClose }) {
  if (!r) return null;
  return (
    <Drawer open printable testid="fo-receipt" onClose={onClose} title={`Receipt · ${r.ref}`}>
      <div className="space-y-5 text-sm" data-testid="fo-receipt-body">
        <div className="flex items-start justify-between">
          <span className="font-display text-xl font-bold text-[#0A234A]">FLEET<span className="text-[#FF5722]">PODS</span></span>
          <span className="text-right font-mono text-xs text-slate-500">{r.ref}<br />{String(r.created_at || "").slice(0, 10)}</span>
        </div>
        <dl className="grid grid-cols-2 gap-3">
          {[["Billed to", company], ["Operator", r.operator], ["Site", `${r.site_name}${r.site_address ? `, ${r.site_address}` : ""}`], ["Stall", r.stall], ["Vehicle", `${r.vehicle}${r.plate ? ` · ${r.plate}` : ""}`], ["Type", r.channel === "B2B" ? "Monthly (30 days)" : `Overnight × ${r.nights || 1}`], ["Dates", `${r.start_date} → ${r.end_date}`], ["Status", `${r.status} · ${payState(r)[0]}`]].map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="font-semibold text-[#0A234A]">{v}</dd></div>)}
        </dl>
        <div className="flex items-center justify-between border-t border-slate-200 pt-4"><span className="font-semibold">Total</span><span className="font-mono text-2xl font-semibold text-[#0A234A]" data-testid="fo-receipt-total">{money(r.amount)}</span></div>
        <p className="text-xs text-slate-400">Amount as recorded on the booking. Taxes, if any, are invoiced by the operator.</p>
        <Button variant="navy" testid="fo-receipt-print" className="fo-noprint" onClick={() => window.print()}><Printer className="h-4 w-4" />Print</Button>
      </div>
    </Drawer>
  );
}

export default function Billing({ data }) {
  const [open, setOpen] = useState(null);
  const rows = useMemo(() => data.reservations.filter(billable), [data.reservations]);
  const total = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
  const paid = rows.filter((r) => Number(r.paid)).reduce((a, r) => a + Number(r.amount || 0), 0);
  const bySite = group(rows, (r) => r.site_name).sort((a, b) => b.value - a.value);
  const byMonth = group(rows, (r) => String(r.created_at || "").slice(0, 7)).sort((a, b) => a.label.localeCompare(b.label)).slice(-6).map((s) => ({ ...s, label: new Date(`${s.label}-01T00:00:00`).toLocaleString(undefined, { month: "short", year: "numeric" }) }));
  const csv = () => downloadFile(`fleetpods-billing-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows, COLS));
  return (
    <>
      <PageHead eyebrow="Money" title="Billing & Spend" sub="Every parking charge from your bookings, what's been settled, and where the money goes.">
        <Button testid="fo-billing-csv" onClick={csv} disabled={!rows.length}><Download className="h-4 w-4" />Export CSV</Button>
      </PageHead>
      <div className="fo-rise fo-rise-1 mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi testid="fo-billing-total" label="Total billed" value={money(total)} />
        <Kpi testid="fo-billing-paid" label="Settled" value={money(paid)} />
        <Kpi testid="fo-billing-outstanding" label="Outstanding" value={money(total - paid)} tone={total - paid > 0 ? "accent" : undefined} />
        <Kpi testid="fo-billing-month" label="This month" value={money(data.kpis.spend_month)} />
      </div>
      {rows.length ? (
        <>
          <div className="fo-rise fo-rise-2 mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Spend by site" testid="fo-billing-by-site"><div className="p-5"><MiniBars series={bySite} testid="fo-billing-site-bars" /></div></Panel>
            <Panel title="Spend by month" testid="fo-billing-by-month"><div className="p-5"><MiniBars series={byMonth} color="bg-[#FF5722]" testid="fo-billing-month-bars" /></div></Panel>
          </div>
          <Panel className="fo-rise fo-rise-3" title="Charges" testid="fo-billing-panel">
            <DataTable head={["Ref", "Operator · site", "Vehicle", "Type", "Dates", "Amount", "Payment", ""]} testid="fo-billing-table">
              {data.reservations.filter(billable).map((r) => { const [label, tone] = payState(r); return (
                <tr key={r.id} data-testid={`fo-billing-row-${r.id}`}>
                  <Td mono strong>{r.ref}</Td>
                  <Td>{r.operator} · {r.site_name}</Td>
                  <Td>{r.vehicle}</Td>
                  <Td>{r.channel === "B2B" ? "monthly" : `${r.nights || 1} night${Number(r.nights) === 1 ? "" : "s"}`}</Td>
                  <Td mono>{r.start_date} → {r.end_date}</Td>
                  <Td mono strong>{money(r.amount)}</Td>
                  <Td><Tag tone={tone}>{label}</Tag></Td>
                  <Td className="text-right"><Button variant="ghost" testid={`fo-billing-receipt-${r.id}`} onClick={() => setOpen(r)}><Receipt className="h-4 w-4" />Receipt</Button></Td>
                </tr>
              ); })}
            </DataTable>
          </Panel>
        </>
      ) : <EmptyState icon={Receipt} testid="fo-billing-empty" title="No charges yet" body="Charges appear here as soon as you book parking." />}
      <ReceiptView r={open} company={data.company} onClose={() => setOpen(null)} />
    </>
  );
}
